import { useEffect, useState } from 'react';

import type { ScheduleExpectationSummary } from '@/types/schedule';
import { Card, Drawer, Pill } from '@/ui';
import pluralize from '@/utils/pluralize';

import './styles.css';

import { buildTimelineRow, HORIZON_WEEKS } from './timelinePreview';
import type { Props } from './types';

const WEEK_NUMBERS = Array.from({ length: HORIZON_WEEKS }, (_, i) => i + 1);

const formatCycleTime = (expectation: ScheduleExpectationSummary) =>
  pluralize(expectation.cycle_time, expectation.time_unit);

const formatLookAhead = (expectation: ScheduleExpectationSummary) => {
  if (expectation.look_ahead_cycles === 0) return 'None';
  const inCycleUnit = expectation.look_ahead_cycles * expectation.cycle_time;
  return `${pluralize(expectation.look_ahead_cycles, 'cycle')} (${formatCycleTime({ ...expectation, cycle_time: inCycleUnit })})`;
};

const formatMaxAttendance = (expectation: ScheduleExpectationSummary) =>
  expectation.max_attendances == null ? 'None' : String(expectation.max_attendances);

const ScheduleDetailPanel = ({ schedule, onClose }: Props) => {
  // Headless UI unmounts children while the drawer transitions closed, so the
  // last-open schedule is retained here to avoid the panel flashing empty
  // during the close animation.
  const [displaySchedule, setDisplaySchedule] = useState(schedule);
  useEffect(() => {
    if (schedule) setDisplaySchedule(schedule);
  }, [schedule]);

  const hasPrograms = (displaySchedule?.programs.length ?? 0) > 0;
  const expectations = displaySchedule?.expectations ?? [];

  return (
    <Drawer
      open={!!schedule}
      onClose={onClose}
      className="w-[940px]"
      title={
        displaySchedule && (
          <div className="flex items-center gap-3">
            <span>{displaySchedule.name}</span>
            <span className="rounded-md border border-uniform-gray-200 dark:border-uniform-gray-700 px-2 py-0.5 text-xs font-normal text-uniform-gray-400 dark:text-uniform-gray-500">
              Rename
            </span>
          </div>
        )
      }>
      {displaySchedule && (
        <div className="flex flex-col gap-7">
          <div className="flex flex-col gap-3">
            <div className="text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
              {[
                pluralize(expectations.length, 'expectation'),
                `${pluralize(displaySchedule.patient_count, 'patient')} on this schedule`
              ].join(' · ')}
            </div>
            {hasPrograms && (
              <div className="flex flex-wrap gap-1.5">
                {displaySchedule.programs.map((program) => (
                  <Pill key={program.id} size="sm" variant="outline" color="neutral">
                    {program.name}
                  </Pill>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2.5">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="text-sm font-semibold text-uniform-gray-900 dark:text-uniform-gray-100">
                Schedule expectations
              </h3>
              <span className="text-sm text-uniform-gray-400 dark:text-uniform-gray-500">
                Add expectation
              </span>
            </div>
            <Card padding="none">
              {expectations.length === 0 ? (
                <p className="px-4 py-6 text-sm text-uniform-gray-400 dark:text-uniform-gray-500">
                  No current expectations
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-uniform-gray-50 dark:bg-uniform-gray-800">
                        <th className="border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-2.5 text-left text-[0.68rem] font-semibold uppercase tracking-wide text-uniform-gray-400 dark:text-uniform-gray-500">
                          Appointment type
                        </th>
                        <th className="whitespace-nowrap border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-2.5 text-right text-[0.68rem] font-semibold uppercase tracking-wide text-uniform-gray-400 dark:text-uniform-gray-500">
                          Cycle time
                        </th>
                        <th className="whitespace-nowrap border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-2.5 text-right text-[0.68rem] font-semibold uppercase tracking-wide text-uniform-gray-400 dark:text-uniform-gray-500">
                          Frequency
                        </th>
                        <th className="whitespace-nowrap border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-2.5 text-right text-[0.68rem] font-semibold uppercase tracking-wide text-uniform-gray-400 dark:text-uniform-gray-500">
                          Look-ahead cycles
                        </th>
                        <th className="whitespace-nowrap border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-2.5 text-right text-[0.68rem] font-semibold uppercase tracking-wide text-uniform-gray-400 dark:text-uniform-gray-500">
                          Max attendance
                        </th>
                        <th className="w-11 border-b border-uniform-gray-100 dark:border-uniform-gray-700" />
                      </tr>
                    </thead>
                    <tbody>
                      {expectations.map((expectation) => (
                        <tr key={expectation.appointment_type_id}>
                          <td className="border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-3 font-medium text-uniform-gray-800 dark:text-uniform-gray-200">
                            {expectation.appointment_type_name}
                          </td>
                          <td className="whitespace-nowrap border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-3 text-right tabular-nums text-uniform-gray-700 dark:text-uniform-gray-300">
                            {formatCycleTime(expectation)}
                          </td>
                          <td className="whitespace-nowrap border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-3 text-right tabular-nums text-uniform-gray-700 dark:text-uniform-gray-300">
                            {pluralize(expectation.frequency, 'appointment')}
                          </td>
                          <td className="whitespace-nowrap border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-3 text-right tabular-nums text-uniform-gray-700 dark:text-uniform-gray-300">
                            {formatLookAhead(expectation)}
                          </td>
                          <td className="whitespace-nowrap border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-3 text-right tabular-nums text-uniform-gray-700 dark:text-uniform-gray-300">
                            {formatMaxAttendance(expectation)}
                          </td>
                          <td className="border-b border-uniform-gray-100 dark:border-uniform-gray-700 px-4 py-3 text-right text-uniform-gray-300 dark:text-uniform-gray-600">
                            ⋯
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {expectations.length > 0 && (
            <div className="flex flex-col gap-2.5">
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="text-sm font-semibold text-uniform-gray-900 dark:text-uniform-gray-100">
                  First {HORIZON_WEEKS} weeks of care
                </h3>
                <span className="text-xs text-uniform-gray-400 dark:text-uniform-gray-500">
                  Preview generated from the fields above
                </span>
              </div>
              <Card padding="none">
                <div className="flex flex-col gap-2.5 px-5 pb-4 pt-4">
                  <div className="flex items-end gap-3">
                    <div className="w-[190px] shrink-0" />
                    <div className="flex flex-1">
                      {WEEK_NUMBERS.map((week) => (
                        <div
                          key={week}
                          className="flex-1 text-center text-[0.68rem] tabular-nums text-uniform-gray-400 dark:text-uniform-gray-500">
                          {week}
                        </div>
                      ))}
                    </div>
                  </div>

                  {expectations.map((expectation) => {
                    const row = buildTimelineRow(expectation);
                    return (
                      <div key={row.appointmentTypeId} className="flex items-center gap-3">
                        <div className="w-[190px] shrink-0 truncate text-xs font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
                          {row.appointmentTypeName}
                        </div>
                        <div
                          className="schedule-timeline-track relative h-9 flex-1 overflow-hidden rounded-md bg-uniform-gray-50 dark:bg-uniform-gray-800"
                          style={{
                            backgroundImage: `repeating-linear-gradient(to right, var(--schedule-timeline-gridline) 0 1px, transparent 1px calc(100% / ${HORIZON_WEEKS}))`
                          }}>
                          <div
                            className="absolute inset-y-0 left-0 border-r-2 border-uniform-green-500 bg-uniform-green-50 dark:bg-uniform-green-800"
                            style={{ width: `${row.advanceWidthPct}%` }}
                          />
                          {row.blocks.map((block, index) => (
                            <div
                              key={index}
                              className="absolute inset-y-0 flex items-center justify-evenly border-l border-dashed border-uniform-gray-200 dark:border-uniform-gray-700"
                              style={{ left: `${block.leftPct}%`, width: `${block.widthPct}%` }}>
                              {Array.from({ length: block.dotCount }, (_, dotIndex) => (
                                <div
                                  key={dotIndex}
                                  className="h-[9px] w-[9px] rounded-full bg-uniform-green-600"
                                />
                              ))}
                            </div>
                          ))}
                          {row.hasTail && (
                            <div
                              className="absolute inset-y-0 right-0 flex items-center pl-2.5 text-[0.68rem] text-uniform-gray-400 dark:text-uniform-gray-500"
                              style={{ left: `${row.tailLeftPct}%` }}>
                              {row.tailLabel}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <div className="ml-[202px] flex flex-wrap items-center gap-4 pt-1.5 text-[0.7rem] text-uniform-gray-500 dark:text-uniform-gray-400">
                    <div className="flex items-center gap-1.5">
                      <span className="h-[9px] w-[9px] rounded-full bg-uniform-green-600" />
                      Expected appointment
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-4 rounded-[3px] border border-uniform-green-500 bg-uniform-green-50 dark:bg-uniform-green-800" />
                      Scheduled in advance
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-4 border-t border-dashed border-uniform-gray-300 dark:border-uniform-gray-600" />
                      Cycle boundary
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default ScheduleDetailPanel;
